<!--
  A WRAPPING GRID OF POSTERS WITH THE RAIL'S MANNERS — one tile experience
  everywhere (the owner, 2026-08-17: "every tile, whether it's on a rail or
  not, does the focus and pop-up… actor pages, Collections, filter by genre").

  What a Poster already brings for free on any surface: hover/first-tap focus,
  the ring and lift, tap-twice-to-open (`tapVerdict`), the spotlight
  publication. What a bare grid was missing is a HOME for the details strip —
  so this wrapper gives it one, per ROW: the strip is a full-width grid item
  placed on the implicit row right under the focused tile's row, pushing the
  rows below on the same 0fr→1fr clock the rails use (SpotlightStrip.svelte,
  one shared implementation). That per-row placement is how a wrapping grid of
  five rows answers "under the row you are looking at" instead of "at the very
  bottom of two screenfuls".

  THE PLACEMENT IS EXPLICIT, THE TILES STAY AUTO. Grid placement puts the
  explicitly-positioned strip first and flows the auto tiles around it: the
  rows above the strip do not move, the rows below step down exactly one row —
  the same push a Rail's strip performs on the page. The row index is measured
  from the tiles' own offsetTop (clustered, because sub-pixel layout makes
  same-row tops differ by hairs) and re-measured when focus moves or the grid
  re-wraps at a new width.

  Parked, not removed, when the focus leaves: the strip closes where it stands
  (a removal would snap the rows shut in one frame — the same reason Rail keeps
  its strip in the DOM), and only then moves back to the grid's end, where a
  zero-height row is invisible below the last line of tiles.

  DELIBERATELY NO `--tile-open` DECLARED — so no trailer expansion here. A
  focused tile in a wrapping grid that widened to 16:9 would reflow every row
  after it (Poster.svelte's header owns that argument); the container decides,
  and this container declines. Rails keep the full behaviour.
-->
<script lang="ts">
  import type { Snippet } from 'svelte';
  import SpotlightStrip from './SpotlightStrip.svelte';
  import { settledFocus } from './focus.svelte';

  interface Props {
    children: Snippet;
  }
  let { children }: Props = $props();

  let grid = $state<HTMLDivElement | null>(null);
  /** The 1-based implicit grid row the strip occupies; null = parked at the end. */
  let stripRow = $state<number | null>(null);
  let parkTimer: ReturnType<typeof setTimeout> | null = null;

  /** Same-row offsetTops differ by fractions of a pixel; cluster within 4px. */
  const ROW_EPSILON = 4;

  function place(): void {
    const el = grid;
    if (!el) return;
    /* `.chosen`, not `.focused`: the strip reveals for the SETTLED focus
       (SpotlightStrip.svelte), so the row it occupies is measured on the same
       clock — this keeps the offsetTop sweep below OFF the live scroll path,
       where it was a forced layout per focus handover mid-fling. */
    const focused = el.querySelector(':scope > .poster.chosen');
    if (!(focused instanceof HTMLElement)) {
      /* Focus left this grid: let the strip CLOSE where it stands (the
         `strip-host` rule is already animating it shut), then park it after
         the reveal has finished so the rows settle once, not twice. */
      if (stripRow !== null && parkTimer === null) {
        parkTimer = setTimeout(() => {
          parkTimer = null;
          stripRow = null;
        }, 300);
      }
      return;
    }
    if (parkTimer !== null) {
      clearTimeout(parkTimer);
      parkTimer = null;
    }
    const top = focused.offsetTop;
    /* How many distinct tile rows sit ABOVE the focused one. Tiles below the
       already-placed strip have shifted a row down, but their tops are larger
       than the focused tile's, so they never enter this count — the answer is
       stable whether or not the strip is currently mid-grid. */
    const above: number[] = [];
    for (const child of el.children) {
      if (!(child instanceof HTMLElement) || !child.classList.contains('poster')) continue;
      const t = child.offsetTop;
      if (t < top - ROW_EPSILON) above.push(t);
    }
    above.sort((a, b) => a - b);
    let rows = 0;
    let last = Number.NEGATIVE_INFINITY;
    for (const t of above) {
      if (t - last > ROW_EPSILON) {
        rows += 1;
        last = t;
      }
    }
    /* Rows are 1-based grid lines: focused row = rows + 1, the strip under it. */
    stripRow = rows + 2;
  }

  /* Re-measure when the settled focus moves… */
  $effect(() => {
    void settledFocus.id;
    place();
    return () => {
      if (parkTimer !== null) {
        clearTimeout(parkTimer);
        parkTimer = null;
      }
    };
  });

  /* …and when the grid re-wraps (a resize changes the column count, so the
     focused tile may live on a different row at the same scroll position). */
  $effect(() => {
    const el = grid;
    if (!el || typeof ResizeObserver !== 'function') return;
    let frame: number | null = null;
    const ro = new ResizeObserver(() => {
      if (frame !== null || typeof requestAnimationFrame !== 'function') return;
      frame = requestAnimationFrame(() => {
        frame = null;
        place();
      });
    });
    ro.observe(el);
    return () => {
      if (frame !== null) cancelAnimationFrame(frame);
      ro.disconnect();
    };
  });
</script>

<!-- `strip-host` is the strip's opening contract — see SpotlightStrip.svelte. -->
<div class="grid strip-host" bind:this={grid}>
  {@render children()}
  <div class="striprow" style:grid-row={stripRow === null ? null : `${stripRow}`}>
    <SpotlightStrip />
  </div>
</div>

<style>
  /* The one poster-grid recipe every surface already used, declared once. */
  .grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(var(--tile-min), 1fr));
    gap: 18px var(--gap);
  }
  @media (max-width: 767px) {
    .grid {
      --tile-min: min(25dvw, 148px);
      --gap: 8px;
      row-gap: 14px;
    }
  }
  /*
   * DEPTH MUST BE FREE HERE, because Browse pages this grid to a category's
   * true end (Animation is ~2,700 tiles — measured 2026-08-17) and Chromium
   * pays a per-tile bookkeeping cost on every produced frame for every tile
   * it keeps rendered. Measured at 6x CPU throttle (headless software
   * raster, 1440x900, worst case on purpose): idle frame time 90ms at 150
   * tiles, 167ms at 400, 288ms at 750; scrolling 143/243/352ms. With this
   * rule the same 750-tile grid reads 79ms idle / 138ms scrolling — the
   * 150-tile floor, at five times the depth — because offscreen tiles skip
   * style/layout/paint work entirely.
   *
   * This is why the grid does NOT window or recycle its DOM: every appended
   * tile stays an ordinary focus citizen (the d-pad's rect measuring, the
   * strip's row arithmetic), and the rendering cost is bounded by the glass
   * instead of the category. Geometry survives the skipping — offsetTop and
   * client rects stay real, which `place()` above depends on; the 300px
   * estimate stands in only for rows far off screen, and `auto` remembers a
   * tile's true size once it has rendered. Browsers without the property
   * (Safari 16) simply ignore both lines.
   */
  .grid > :global(.poster) {
    content-visibility: auto;
    contain-intrinsic-size: auto 300px;
  }
  /*
   * NOT ON A TELEVISION. A TV never scrolls this grid continuously — it moves
   * by d-pad presses (scrolled.svelte.ts refuses to even track there), so the
   * per-frame saving above buys it nothing, while the cost lands exactly
   * where a TV hurts most: each press scrolls a row of skipped tiles into
   * relevance and their first render (plus the 300px-estimate correction)
   * happens ON the press path. Measured at 6x, 549 tiles: 2,605ms a press
   * with the rule, 1,454ms without. Fully rendered tiles are the cheaper
   * bargain under a remote.
   */
  :global(html[data-tv='on']) .grid > :global(.poster) {
    content-visibility: visible;
    contain-intrinsic-size: none;
  }
  /* The strip's row: full width, so the implicit row it claims is its own and
     the auto-flowing tiles step past it. Parked (no explicit row) it lands
     after the last tile, where a collapsed strip is a zero-height row. */
  .striprow {
    grid-column: 1 / -1;
    min-width: 0;
  }
</style>
